import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Title as ChartTitle,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
// import ChartDataLabels from "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo, useState } from "react";
import { Doughnut, Line } from "react-chartjs-2";
import {
  Activity,
  AlertCircle,
  Calendar,
  ChartDots,
  ChartLine,
  ChartPie,
  School,
  TrendingUp,
  Users,
} from "tabler-icons-react";
import {
  useGetAllElevesQuery,
  useGetStudentActivityQuery,
} from "../../redux/features/eleveApi";
import {
  useGetParentsQuery,
  useGetProspectsQuery,
  useGetProspectsStatsQuery,
} from "../../redux/features/parentApi";

import AppUsageByHourChart from "./components/AppUsageByHourChart";
import AppUsageChart, {
  UsageMetricCard,
  useAppUsageData,
} from "./components/AppUsageChart";
import ClientConversionChart from "./components/ClientConversionChart";
import ParentEngagementChart from "./components/ParentEngagementChart";
import StudentActivityChart from "./components/StudentActivityChart.jsx";

// Register chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  // ChartDataLabels,
);

// Register dayjs plugins
dayjs.extend(isBetween);
dayjs.locale("fr");

const FUNNEL_CONFIG = [
  {
    key: "CONTACT",
    label: "Contacts",
    shortLabel: "Contact",
    color: "blue",
    hex: "#3b82f6",
  },
  {
    key: "PROSPECT",
    label: "Prospects",
    shortLabel: "Prospect",
    color: "yellow",
    hex: "#f59e0b",
  },
  {
    key: "USER",
    label: "Utilisateurs",
    shortLabel: "Utilisateur",
    color: "teal",
    hex: "#14b8a6",
  },
  {
    key: "USER_ACTIVE",
    label: "Utilisateurs activés",
    shortLabel: "Actif",
    color: "green",
    hex: "#10b981",
  },
  {
    key: "CLIENT",
    label: "Clients",
    shortLabel: "Client",
    color: "red",
    hex: "#ef4444",
  },
  {
    key: "CLIENT_ACTIF",
    label: "Clients actifs",
    shortLabel: "Actif 30j",
    color: "orange",
    hex: "#f97316",
  },
  {
    key: "CLIENT_ENGAGE",
    label: "Clients engagés",
    shortLabel: "Engagé 40j",
    color: "grape",
    hex: "#a855f7",
  },
  {
    key: "CLIENT_IMPACTE",
    label: "Clients impactés 40J",
    shortLabel: "Impacté",
    color: "violet",
    hex: "#7c3aed",
  },
];

const CLIENT_STAGE_KEYS = [
  "CLIENT",
  "CLIENT_ACTIF",
  "CLIENT_ENGAGE",
  "CLIENT_IMPACTE",
];

const StatsSummaryCard = ({
  title,
  value,
  icon,
  color,
  subtext,
  switchProps,
}) => (
  <Card
    shadow="sm"
    p="xl"
    radius="lg"
    sx={{
      border: "none",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow:
          "0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02)",
      },
    }}
  >
    <Group position="apart" mb="md" align="flex-start">
      <Box>
        <Text
          size="sm"
          color="dimmed"
          transform="uppercase"
          weight={600}
          letterSpacing={0.5}
        >
          {title}
        </Text>
        <Text weight={800} size={32} mt={4} sx={{ lineHeight: 1.1 }}>
          {value}
        </Text>
      </Box>
      <ThemeIcon
        color={color}
        size={54}
        radius="xl"
        variant="light"
        sx={{ opacity: 0.8 }}
      >
        {icon}
      </ThemeIcon>
    </Group>
    {subtext && (
      <Text size="sm" color="dimmed" mb={switchProps ? "md" : 0}>
        {subtext}
      </Text>
    )}
    {switchProps && (
      <Box mt="sm" pt="sm" sx={{ borderTop: "1px solid #f1f3f5" }}>
        <Switch
          label={switchProps.label}
          checked={switchProps.checked}
          onChange={switchProps.onChange}
          size="sm"
          color={color}
          styles={{
            label: { fontSize: "12px", fontWeight: 500, color: "#495057" },
          }}
        />
      </Box>
    )}
  </Card>
);

const StatistiquesPage = () => {
  const [userFilter, setUserFilter] = useState("all"); // 'all', 'students', 'parents'
  const [periodFilter, setPeriodFilter] = useState("month");
  const [customDates, setCustomDates] = useState([null, null]);
  const [includeStudentsInActiveUsers, setIncludeStudentsInActiveUsers] =
    useState(true);
  const [provenanceFilter, setProvenanceFilter] = useState("all"); // 'all', 'Genimi', 'Senassurance'
  const [funnelByPeriod, setFunnelByPeriod] = useState(true); // false = all-time, true = période sélectionnée
  const [showAdvancedSections, setShowAdvancedSections] = useState(false);

  // Fetch with limit parameter (10000) to ensure we get all records
  // This was critical for getting all parents in previous debugging
  const {
    data: elevesData,
    isLoading: isLoadingEleves,
    error: errorEleves,
  } = useGetAllElevesQuery({ limit: 50000 });

  const {
    data: parentsData,
    isLoading: isLoadingParents,
    error: errorParents,
  } = useGetParentsQuery({ limit: 50000 });

  // Liste prospects/contacts pour le funnel par période (source contenant computed_category)
  const {
    data: prospectsListData,
    isLoading: isLoadingProspectsList,
    error: errorProspectsList,
  } = useGetProspectsQuery({ page: 1, limit: 50000 });

  // Source de vérité pour les compteurs du funnel (même endpoint que ProspectIndexPage)
  const { data: prospectsStatsData, isLoading: isLoadingFunnelStats } =
    useGetProspectsStatsQuery(undefined, { refetchOnMountOrArgChange: true });

  const isLoading = isLoadingEleves || isLoadingParents;
  const error = errorEleves || errorParents;

  const dateRange = useMemo(() => {
    const now = dayjs();
    let start = now.startOf("month");
    let end = now.endOf("month");

    switch (periodFilter) {
      case "today":
        start = now.startOf("day");
        end = now.endOf("day");
        break;
      case "yesterday":
        start = now.subtract(1, "day").startOf("day");
        end = now.subtract(1, "day").endOf("day");
        break;
      case "last-week":
        start = now.subtract(1, "week").startOf("week");
        end = now.subtract(1, "week").endOf("week");
        break;
      case "last-month":
        start = now.subtract(1, "month").startOf("month");
        end = now.subtract(1, "month").endOf("month");
        break;
      case "week":
        start = now.startOf("week");
        end = now.endOf("week");
        break;
      case "month":
        start = now.startOf("month");
        end = now.endOf("month");
        break;
      case "year":
        start = now.startOf("year");
        end = now.endOf("year");
        break;
      case "custom":
        if (customDates[0] && customDates[1]) {
          start = dayjs(customDates[0]);
          end = dayjs(customDates[1]).endOf("day");
        } else {
          // Default to month if custom not selected
          start = now.startOf("month");
          end = now.endOf("month");
        }
        break;
      case "all":
        start = dayjs("2020-01-01"); // Sufficiently past date
        end = now.endOf("day");
        break;
      default:
        break;
    }
    return { start, end };
  }, [periodFilter, customDates]);

  // Activité des élèves (diagnostics) sur la période sélectionnée
  const { data: studentActivityData, isLoading: isLoadingStudentActivity } =
    useGetStudentActivityQuery(
      {
        startDate:
          periodFilter === "custom" && (!customDates[0] || !customDates[1])
            ? undefined
            : dateRange.start.format("YYYY-MM-DD"),
        endDate:
          periodFilter === "custom" && (!customDates[0] || !customDates[1])
            ? undefined
            : dateRange.end.format("YYYY-MM-DD"),
      },
      { refetchOnMountOrArgChange: true },
    );

  // Core Data Processing for Chart
  const processedData = useMemo(() => {
    const { start, end } = dateRange;
    const labels = [];
    const dataMap = new Map();

    // Generate labels and initialize map based on period
    let current = start.clone();

    // Logic to group data
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    if (isYearOrAll) {
      // Group by Month
      let currMonth = start.clone().startOf("month");
      while (currMonth.isBefore(end) || currMonth.isSame(end, "month")) {
        const label = currMonth.format("MMM YYYY");
        labels.push(label);
        dataMap.set(label, 0);
        currMonth = currMonth.add(1, "month");
      }
    } else {
      // Group by Day
      while (current.isBefore(end) || current.isSame(end, "day")) {
        const label = current.format("DD MMM");
        labels.push(label);
        dataMap.set(label, 0);
        current = current.add(1, "day");
      }
    }

    // Helper to process a list of items
    const processItems = (items, isParent = false) => {
      if (!items) return 0;
      let count = 0;
      items.forEach((item) => {
        // Filtrer par provenance si c'est un parent
        if (isParent && provenanceFilter !== "all") {
          if (
            !item.provenance ||
            item.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
          ) {
            return;
          }
        }

        // Safe date access with fallback for consistency
        const dateStr =
          item.createdAt ||
          item.created_at ||
          item.date_inscription ||
          item?.parent?.created_at;

        if (!dateStr) return;

        const date = dayjs(dateStr);
        if (!date.isValid()) return;

        if (date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")) {
          const label = isYearOrAll
            ? date.format("MMM YYYY")
            : date.format("DD MMM");

          if (dataMap.has(label)) {
            dataMap.set(label, dataMap.get(label) + 1);
          }
          count++;
        }
      });
      return count;
    };

    let totalFiltered = 0;

    // Apply User Filter for Chart Data
    // Note: We might want separate totals for the summary cards
    // but the chart itself follows the userFilter state.
    if (userFilter === "all" || userFilter === "students") {
      totalFiltered += processItems(elevesData?.data, false);
    }
    if (userFilter === "all" || userFilter === "parents") {
      totalFiltered += processItems(parentsData?.data, true);
    }

    return {
      labels,
      chartValues: labels.map((l) => dataMap.get(l)),
      totalFiltered,
    };
  }, [
    userFilter,
    dateRange,
    elevesData,
    parentsData,
    periodFilter,
    provenanceFilter,
  ]);

  // Calculate Summary Stats regardless of userFilter for the top cards
  const summaryStats = useMemo(() => {
    const { start, end } = dateRange;
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    const countInPeriod = (items, isParent = false) => {
      if (!items) return 0;
      return items.filter((item) => {
        // Filtrer par provenance si c'est un parent
        if (isParent && provenanceFilter !== "all") {
          if (
            !item.provenance ||
            item.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
          ) {
            return false;
          }
        }

        const dateStr =
          item.createdAt ||
          item.created_at ||
          item.date_inscription ||
          item?.parent?.created_at;
        if (!dateStr) return false;
        const date = dayjs(dateStr);
        return (
          date.isValid() &&
          date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")
        );
      }).length;
    };

    return {
      students: countInPeriod(elevesData?.data, false),
      parents: countInPeriod(parentsData?.data, true),
    };
  }, [elevesData, parentsData, dateRange, periodFilter, provenanceFilter]);

  // Data processing for Doughnut Chart (Levels)
  const levelByChartData = useMemo(() => {
    if (!elevesData?.data) return { labels: [], datasets: [] };
    const { start, end } = dateRange;
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    const levelCounts = {};

    elevesData.data.forEach((eleve) => {
      const dateStr =
        eleve.createdAt || eleve.created_at || eleve.date_inscription;
      if (!dateStr) return;

      const date = dayjs(dateStr);
      if (!date.isValid()) return;

      if (!date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")) {
        return;
      }

      // Assuming eleve.classe.name holds the grade/level info (e.g., "CM2", "CI")
      // Check for 'classe' object or maybe 'niveaux'
      const levelName =
        eleve.classe?.name || eleve.classe?.sub_title || "Non classé";
      levelCounts[levelName] = (levelCounts[levelName] || 0) + 1;
    });

    const labels = Object.keys(levelCounts);
    const data = Object.values(levelCounts);

    // Sort by count descending for better visual
    const sortedIndices = data
      .map((_, i) => i)
      .sort((a, b) => data[b] - data[a]);
    const sortedLabels = sortedIndices.map((i) => labels[i]);
    const sortedData = sortedIndices.map((i) => data[i]);

    // Palette de couleurs étendue pour supporter tous les niveaux scolaires
    const colorPalette = [
      "#3b82f6", // Blue-500
      "#10b981", // Emerald-500
      "#f59e0b", // Amber-500
      "#ef4444", // Red-500
      "#8b5cf6", // Violet-500
      "#ec4899", // Pink-500
      "#06b6d4", // Cyan-500
      "#6366f1", // Indigo-500
      "#84cc16", // Lime-500
      "#14b8a6", // Teal-500
      "#f97316", // Orange-500
      "#db2777", // Pink-600
      "#7c3aed", // Violet-600
      "#0ea5e9", // Sky-500
      "#22c55e", // Green-500
      "#eab308", // Yellow-500
      "#a855f7", // Purple-500
      "#14b8a6", // Teal-500
      "#f43f5e", // Rose-500
      "#06b6d4", // Cyan-500
    ];

    // Générer des couleurs supplémentaires si nécessaire
    const backgroundColor = sortedLabels.map((_, index) => {
      if (index < colorPalette.length) {
        return colorPalette[index];
      }
      // Générer une couleur aléatoire pour les niveaux supplémentaires
      const hue = (index * 137.508) % 360; // Golden angle pour distribution uniforme
      return `hsl(${hue}, 65%, 55%)`;
    });

    return {
      labels: sortedLabels,
      datasets: [
        {
          data: sortedData,
          backgroundColor,
          borderColor: "#ffffff",
          borderWidth: 2,
        },
      ],
    };
  }, [elevesData, dateRange, periodFilter]);

  // Calculate total app usage for summary card
  const totalAppUsage = useMemo(() => {
    const { start, end } = dateRange;
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    if (!parentsData?.data) return 0;

    return parentsData.data.filter((parent) => {
      // Filtrer par provenance
      if (provenanceFilter !== "all") {
        if (
          !parent.provenance ||
          parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
        ) {
          return false;
        }
      }

      const usageDate = parent.last_credit_usage;
      if (!usageDate) return false;

      const date = dayjs(usageDate);
      if (!date.isValid()) return false;

      return date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]");
    }).length;
  }, [dateRange, parentsData, periodFilter, provenanceFilter]);

  // Calculate total active users (new registrations + credit usage + connections without credit)
  const totalActiveUsers = useMemo(() => {
    const { start, end } = dateRange;
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    if (!elevesData?.data && !parentsData?.data) return 0;

    // Set pour éviter les doublons
    const activeUserIds = new Set();

    // 1. Nouveaux inscrits (élèves + parents) dans la période
    if (includeStudentsInActiveUsers && elevesData?.data) {
      elevesData.data.forEach((eleve) => {
        const dateStr =
          eleve.createdAt || eleve.created_at || eleve.date_inscription;
        if (!dateStr) return;

        const date = dayjs(dateStr);
        if (
          date.isValid() &&
          date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")
        ) {
          activeUserIds.add(`eleve-${eleve.id}`);
        }
      });
    }

    if (parentsData?.data) {
      parentsData.data.forEach((parent) => {
        // Filtrer par provenance
        if (provenanceFilter !== "all") {
          if (
            !parent.provenance ||
            parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
          ) {
            return;
          }
        }

        const dateStr =
          parent.createdAt || parent.created_at || parent.date_inscription;
        if (!dateStr) return;

        const date = dayjs(dateStr);
        if (
          date.isValid() &&
          date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")
        ) {
          activeUserIds.add(`parent-${parent.id}`);
        }
      });

      // 2. Parents qui ont utilisé du crédit dans la période
      parentsData.data.forEach((parent) => {
        // Filtrer par provenance
        if (provenanceFilter !== "all") {
          if (
            !parent.provenance ||
            parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
          ) {
            return;
          }
        }

        const creditDate = parent.last_credit_usage;
        if (!creditDate) return;

        const date = dayjs(creditDate);
        if (
          date.isValid() &&
          date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")
        ) {
          activeUserIds.add(`parent-${parent.id}`);
        }
      });

      // 3. Parents qui se sont connectés dans la période (sans nécessairement utiliser du crédit)
      parentsData.data.forEach((parent) => {
        // Filtrer par provenance
        if (provenanceFilter !== "all") {
          if (
            !parent.provenance ||
            parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
          ) {
            return;
          }
        }

        const loginDate = parent.last_login;
        if (!loginDate) return;

        const date = dayjs(loginDate);
        if (
          date.isValid() &&
          date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")
        ) {
          activeUserIds.add(`parent-${parent.id}`);
        }
      });
    }

    return activeUserIds.size;
  }, [
    dateRange,
    parentsData,
    elevesData,
    periodFilter,
    includeStudentsInActiveUsers,
    provenanceFilter,
  ]);

  // Funnel counts ALL-TIME : source = endpoint backend dédié
  const funnelCounts = useMemo(() => {
    const d = prospectsStatsData?.data ?? {};
    return FUNNEL_CONFIG.reduce((acc, { key }) => {
      acc[key] = d[key] ?? 0;
      return acc;
    }, {});
  }, [prospectsStatsData]);

  // Funnel counts PÉRIODE :
  // - étapes avant client => date d'inscription
  // - étapes client et plus => date de conversion
  const funnelCountsByPeriod = useMemo(() => {
    const counts = FUNNEL_CONFIG.reduce((acc, { key }) => {
      acc[key] = 0;
      return acc;
    }, {});
    const prospects = Array.isArray(prospectsListData?.data)
      ? prospectsListData.data
      : Array.isArray(prospectsListData)
        ? prospectsListData
        : [];
    if (!Array.isArray(prospects) || prospects.length === 0) return counts;

    const { start, end } = dateRange;
    const grain =
      periodFilter === "year" || periodFilter === "all" ? "month" : "day";

    prospects.forEach((parent) => {
      if (provenanceFilter !== "all") {
        if (
          !parent.provenance ||
          parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
        )
          return;
      }
      const category = parent.computed_category || parent.type;
      if (!category || counts[category] === undefined) return;

      const isClientStage = CLIENT_STAGE_KEYS.includes(category);
      const referenceDate = isClientStage
        ? parent.date_conversion || parent.dateConversion
        : parent.created_at || parent.createdAt || parent.date_inscription;

      // Pour les étapes client, on ne retombe jamais sur la date d'inscription.
      if (!referenceDate) return;

      const referenceAt = dayjs(referenceDate);
      const isInPeriod =
        referenceAt.isValid() && referenceAt.isBetween(start, end, grain, "[]");

      if (!isInPeriod) return;

      counts[category]++;
    });

    return counts;
  }, [prospectsListData, dateRange, periodFilter, provenanceFilter]);

  // Source active selon le toggle
  const activeFunnelCounts = funnelByPeriod
    ? funnelCountsByPeriod
    : funnelCounts;

  // Comptes CUMULATIFS : chaque étape inclut les parents à cette étape OU plus avancés
  const funnelCumulative = useMemo(() => {
    const cum = {};
    for (let i = 0; i < FUNNEL_CONFIG.length; i++) {
      cum[FUNNEL_CONFIG[i].key] = FUNNEL_CONFIG.slice(i).reduce(
        (sum, { key }) => sum + (activeFunnelCounts[key] ?? 0),
        0,
      );
    }
    return cum;
  }, [activeFunnelCounts]);

  const chartData = {
    labels: processedData.labels,
    datasets: [
      {
        fill: true,
        label: "Inscriptions",
        data: processedData.chartValues,
        borderColor: "#3b82f6",
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(59, 130, 246, 0.5)");
          gradient.addColorStop(1, "rgba(59, 130, 246, 0.0)");
          return gradient;
        },
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#3b82f6",
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 13 },
        displayColors: false,
        callbacks: {
          label: (context) =>
            `${context.parsed.y} ${context.dataset.label || "Valeurs"}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { maxTicksLimit: 10, color: "#9ca3af" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f3f4f6", borderDash: [5, 5], drawBorder: false },
        ticks: { stepSize: 1, color: "#9ca3af" },
      },
    },
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "right",
        labels: {
          usePointStyle: true,
          padding: 20,
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const dataset = data.datasets[0];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              return data.labels.map((label, i) => {
                const value = dataset.data[i];
                const percentage = ((value / total) * 100).toFixed(1);
                return {
                  text: `${label}: ${percentage}%`,
                  fillStyle: dataset.backgroundColor[i],
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || "";
            const value = context.parsed;
            const total = context.dataset.data.reduce(
              (acc, val) => acc + val,
              0,
            );
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} élèves (${percentage}%)`;
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    maintainAspectRatio: false,
  };

  const PERIOD_LABELS = {
    today: "Aujourd'hui",
    yesterday: "Hier",
    "last-week": "Semaine dernière",
    "last-month": "Mois dernier",
    week: "Cette semaine",
    month: "Ce mois",
    year: "Cette année",
    all: "Tout",
    custom: "Personnalisé",
  };

  const currentPeriodLabel = PERIOD_LABELS[periodFilter] || "Période";

  return (
    <Box
      p={{ base: "md", md: "xl" }}
      mih="100vh"
      sx={{
        background: "#f8f9fa",
      }}
    >
      {/* Header Section */}
      <Box mb="xl" pb="md" sx={{ borderBottom: "1px solid #e9ecef" }}>
        <Flex justify="space-between" align="flex-end" wrap="wrap" gap="xl">
          <Box>
            <Group spacing="sm" mb={8}>
              <ThemeIcon variant="light" color="blue" size={40} radius="md">
                <ChartDots size={24} />
              </ThemeIcon>
              <Title
                order={1}
                sx={{
                  fontSize: "28px",
                  fontWeight: 800,
                  color: "primaryColor",
                }}
              >
                Statistiques & Performances
              </Title>
            </Group>
            <Text size="md" color="dimmed" sx={{ maxWidth: 600 }}>
              Consultez l'activité de vos utilisateurs de manière claire et
              simplifiée.
            </Text>

            <Group spacing="xs" mt="md">
              <Badge color="blue" variant="dot" size="md" radius="sm">
                Période: {currentPeriodLabel}
              </Badge>
              <Badge color="teal" variant="dot" size="md" radius="sm">
                Provenance:{" "}
                {provenanceFilter === "all" ? "Toutes" : provenanceFilter}
              </Badge>
            </Group>
          </Box>

          {/* Global Filters */}
          <Paper
            shadow="sm"
            radius="xl"
            p="xs"
            sx={{ background: "#ffffff", border: "1px solid #f1f3f5" }}
          >
            <Flex gap="sm" wrap="wrap" align="center">
              <Select
                placeholder="Période"
                value={periodFilter}
                onChange={setPeriodFilter}
                data={[
                  { value: "today", label: "Aujourd'hui" },
                  { value: "yesterday", label: "Hier" },
                  { value: "last-week", label: "Semaine dernière" },
                  { value: "last-month", label: "Mois dernier" },
                  { value: "week", label: "Cette semaine" },
                  { value: "month", label: "Ce mois" },
                  { value: "year", label: "Cette année" },
                  { value: "all", label: "Tout" },
                  { value: "custom", label: "Personnalisé" },
                ]}
                icon={<Calendar size={16} />}
                size="sm"
                w={160}
                variant="filled"
                radius="xl"
                styles={{
                  input: { fontWeight: 500, backgroundColor: "#f8f9fa" },
                }}
              />

              {periodFilter === "custom" && (
                <DatePickerInput
                  type="range"
                  placeholder="Sélectionner une période"
                  value={customDates}
                  onChange={setCustomDates}
                  size="sm"
                  locale="fr"
                  w={240}
                  variant="filled"
                  radius="xl"
                  styles={{ input: { backgroundColor: "#f8f9fa" } }}
                />
              )}

              <Select
                placeholder="Provenance"
                value={provenanceFilter}
                onChange={setProvenanceFilter}
                data={[
                  { value: "all", label: "Toutes les provenances" },
                  { value: "Genimi", label: "Genimi" },
                  { value: "Senassurance", label: "Senassurance" },
                ]}
                icon={<Users size={16} />}
                size="sm"
                w={200}
                variant="filled"
                radius="xl"
                styles={{
                  input: { fontWeight: 500, backgroundColor: "#f8f9fa" },
                }}
              />

              <Button
                size="sm"
                variant="subtle"
                color="gray"
                radius="xl"
                onClick={() => {
                  setPeriodFilter("month");
                  setCustomDates([null, null]);
                  setProvenanceFilter("all");
                  setUserFilter("all");
                  setFunnelByPeriod(true);
                }}
              >
                Réinitialiser
              </Button>
            </Flex>
          </Paper>
        </Flex>
      </Box>

      {error && (
        <Alert
          icon={<AlertCircle size={16} />}
          title="Erreur"
          color="red"
          mb="md"
          variant="filled"
        >
          <Text color="white">
            Une erreur est survenue lors du chargement des données.
          </Text>
        </Alert>
      )}

      {/* Summary Cards Section */}
      {/* Première ligne: Inscriptions */}
      <SimpleGrid
        cols={3}
        spacing="lg"
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "sm", cols: 1 },
        ]}
        mb="lg"
      >
        <StatsSummaryCard
          title="Total Inscriptions"
          value={summaryStats.students + summaryStats.parents}
          subtext="Sur la période sélectionnée"
          icon={<TrendingUp size={24} />}
          color="blue"
        />
        <StatsSummaryCard
          title="Nouveaux Élèves"
          value={summaryStats.students}
          subtext="Élèves inscrits"
          icon={<School size={24} />}
          color="teal"
        />
        <StatsSummaryCard
          title="Nouveaux Parents"
          value={summaryStats.parents}
          subtext="Parents inscrits"
          icon={<Users size={24} />}
          color="orange"
        />
      </SimpleGrid>

      {/* Deuxième ligne: Utilisateurs Actifs et Utilisation App */}
      <SimpleGrid
        cols={2}
        spacing="lg"
        breakpoints={[{ maxWidth: "sm", cols: 1 }]}
        mb="xl"
      >
        <StatsSummaryCard
          title="Comptes actifs"
          value={totalActiveUsers}
          subtext="Inscrits + Connexions + Crédits"
          icon={<ChartLine size={24} />}
          color="grape"
          switchProps={{
            label: "Inclure les nouveaux élèves",
            checked: includeStudentsInActiveUsers,
            onChange: (e) =>
              setIncludeStudentsInActiveUsers(e.currentTarget.checked),
          }}
        />
        <StatsSummaryCard
          title="Utilisations de l'app"
          value={totalAppUsage}
          subtext="Parents ayant utilisé des crédits"
          icon={<Activity size={24} />}
          color="violet"
        />
      </SimpleGrid>

      <Divider my="xl" />

      {/* Section: Funnel d'acquisition parents */}
      <Box>
        <Group position="apart" mb={4} align="center">
          <Group spacing="sm">
            <ThemeIcon variant="light" color="blue" size={40} radius="md">
              <TrendingUp size={24} />
            </ThemeIcon>
            <Title
              order={2}
              sx={{ fontSize: "22px", fontWeight: 700, color: "#1c7ed6" }}
            >
              Funnel d'acquisition parents
            </Title>
          </Group>
          <Button
            size="xs"
            variant={funnelByPeriod ? "filled" : "light"}
            color={funnelByPeriod ? "blue" : "gray"}
            leftIcon={<Calendar size={14} />}
            onClick={() => setFunnelByPeriod((v) => !v)}
          >
            {funnelByPeriod
              ? `Funnel filtré (${periodFilter === "today" ? "aujourd'hui" : periodFilter === "yesterday" ? "hier" : periodFilter === "last-week" ? "semaine dernière" : periodFilter === "last-month" ? "mois dernier" : periodFilter === "week" ? "cette semaine" : periodFilter === "month" ? "ce mois" : periodFilter === "year" ? "cette année" : periodFilter === "all" ? "tout" : "personnalisé"})`
              : "Funnel global (non filtré)"}
          </Button>
        </Group>
        <Text size="sm" color="dimmed" mb="xs">
          {funnelByPeriod
            ? "Les étapes avant client sont filtrées par date d'inscription. Les étapes client sont filtrées par date de conversion."
            : "Tous les parents depuis le début — combien ont atteint chaque étape et quel % se convertit."}
        </Text>

        {funnelByPeriod && (
          <Group spacing="xs" mb="lg">
            <Badge color="blue" variant="light">
              Avant client = date d'inscription
            </Badge>
            <Badge color="green" variant="light">
              A partir de client = date de conversion
            </Badge>
          </Group>
        )}

        {(funnelByPeriod ? isLoadingProspectsList : isLoadingFunnelStats) ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
            }}
          >
            <Loader size="lg" />
          </Box>
        ) : funnelByPeriod && errorProspectsList ? (
          <Alert
            icon={<AlertCircle size={16} />}
            title="Erreur"
            color="red"
            mb="md"
          >
            Impossible de charger les données prospects pour le funnel.
          </Alert>
        ) : (
          (() => {
            const totalAll = funnelCumulative[FUNNEL_CONFIG[0].key] || 1;
            return (
              <>
                {/* ── Graphique funnel horizontal ── */}
                <Paper
                  shadow="sm"
                  p="xl"
                  radius="lg"
                  sx={{ backgroundColor: "#ffffff" }}
                  mb="lg"
                >
                  <Stack spacing={8}>
                    {FUNNEL_CONFIG.map(({ key, label, hex }, index) => {
                      const cumCount = funnelCumulative[key] ?? 0;
                      const count = activeFunnelCounts[key] ?? 0;
                      const pct = (cumCount / totalAll) * 100;
                      const prevCum =
                        index > 0
                          ? (funnelCumulative[FUNNEL_CONFIG[index - 1].key] ??
                            0)
                          : null;
                      const convRate =
                        prevCum && prevCum > 0
                          ? ((cumCount / prevCum) * 100).toFixed(1)
                          : null;
                      const convColor = convRate
                        ? parseFloat(convRate) >= 50
                          ? "#10b981"
                          : parseFloat(convRate) >= 20
                            ? "#f97316"
                            : "#ef4444"
                        : "transparent";

                      return (
                        <Box key={key}>
                          {/* Flèche de conversion entre étapes */}
                          {index > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 4,
                                paddingLeft: 8,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 16,
                                  height: 1,
                                  background: "#dee2e6",
                                }}
                              />
                              <Text
                                size="xs"
                                weight={700}
                                sx={{ color: convColor }}
                              >
                                ↓ {convRate}% se convertissent
                              </Text>
                            </Box>
                          )}

                          {/* Ligne de la barre */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            {/* Numéro */}
                            <Text
                              size="xs"
                              color="dimmed"
                              weight={700}
                              sx={{ minWidth: 20, textAlign: "right" }}
                            >
                              {index + 1}
                            </Text>

                            {/* Label */}
                            <Text size="sm" weight={600} sx={{ minWidth: 150 }}>
                              {label}
                            </Text>

                            {/* Barre proportionnelle */}
                            <Box
                              sx={{
                                flex: 1,
                                background: "#f1f3f5",
                                borderRadius: 4,
                                height: 28,
                                position: "relative",
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  height: "100%",
                                  width: `${pct}%`,
                                  background: `linear-gradient(90deg, ${hex}cc, ${hex})`,
                                  borderRadius: 4,
                                  transition: "width 0.8s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  paddingLeft: 10,
                                  minWidth: pct > 5 ? "auto" : 0,
                                }}
                              >
                                {pct > 8 && (
                                  <Text
                                    size="xs"
                                    weight={700}
                                    sx={{
                                      color: "#fff",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {cumCount.toLocaleString("fr-FR")}
                                  </Text>
                                )}
                              </Box>

                              {pct <= 8 && (
                                <Text
                                  size="xs"
                                  weight={700}
                                  sx={{
                                    position: "absolute",
                                    left: 10,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#495057",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {cumCount.toLocaleString("fr-FR")}
                                </Text>
                              )}
                            </Box>

                            {/* % + count brut à droite */}
                            <Box sx={{ minWidth: 110, textAlign: "right" }}>
                              <Text size="sm" weight={800} sx={{ color: hex }}>
                                {pct.toFixed(1)}%
                              </Text>
                              <Text size="xs" color="dimmed">
                                {count.toLocaleString("fr-FR")} parents
                              </Text>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>

                  {/* Légende */}
                  <Group spacing="xl" mt="lg" position="center">
                    <Group spacing={6}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          background: "#6366f1",
                        }}
                      />
                      <Text size="xs" color="dimmed">
                        Barre = % ayant atteint cette étape ou plus
                      </Text>
                    </Group>
                    <Group spacing={6}>
                      <Text size="xs" color="dimmed" weight={600}>
                        N inscrits
                      </Text>
                      <Text size="xs" color="dimmed">
                        = parents actuellement à cette étape exacte
                      </Text>
                    </Group>
                  </Group>
                </Paper>

                {/* ── Funnel horizontal en cards ── */}
                {(() => {
                  // Calcul des taux étape par étape
                  const rates = FUNNEL_CONFIG.map(({ key }, idx) => {
                    if (idx === 0) return null;
                    const cur = activeFunnelCounts[key] ?? 0;
                    const prev =
                      activeFunnelCounts[FUNNEL_CONFIG[idx - 1].key] ?? 0;
                    return prev > 0 ? (cur / prev) * 100 : null;
                  });
                  // Index de la plus faible conversion
                  let lowestIdx = -1,
                    lowestRate = Infinity;
                  rates.forEach((r, i) => {
                    if (r !== null && r < lowestRate) {
                      lowestRate = r;
                      lowestIdx = i;
                    }
                  });

                  return (
                    <Box mt="xl" sx={{ overflowX: "auto", paddingBottom: 8 }}>
                      <Flex align="flex-start" wrap="nowrap" gap={0}>
                        {FUNNEL_CONFIG.map(({ key, shortLabel }, idx) => {
                          const count = activeFunnelCounts[key] ?? 0;
                          const rate = rates[idx];
                          const isLowest = idx === lowestIdx;
                          const rateColor =
                            rate === null
                              ? "inherit"
                              : rate >= 70
                                ? "#16a34a"
                                : rate >= 40
                                  ? "#f97316"
                                  : "#ef4444";

                          return (
                            <Flex
                              key={key}
                              align="center"
                              sx={{ flex: "0 0 auto" }}
                            >
                              {/* Carte — espace badge réservé sur toutes les cartes */}
                              <Box
                                sx={{
                                  position: "relative",
                                  paddingTop: 22,
                                }}
                              >
                                {isLowest && (
                                  <Badge
                                    color="red"
                                    variant="filled"
                                    size="sm"
                                    radius="sm"
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: "50%",
                                      transform: "translateX(-50%)",
                                      whiteSpace: "nowrap",
                                      zIndex: 1,
                                    }}
                                  >
                                    Plus faible conversion
                                  </Badge>
                                )}
                                <Box
                                  sx={{
                                    width: 140,
                                    minHeight: 115,
                                    padding: 16,
                                    borderRadius: 12,
                                    border: isLowest
                                      ? "1px solid #ffc9c9"
                                      : "1px solid #f1f3f5",
                                    background: isLowest
                                      ? "#fff5f5"
                                      : "#ffffff",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                                    boxSizing: "border-box",
                                    transition: "transform 0.2s ease",
                                    "&:hover": {
                                      transform: "translateY(-2px)",
                                    },
                                  }}
                                >
                                  <Text
                                    size="sm"
                                    weight={600}
                                    color="dimmed"
                                    mb={4}
                                  >
                                    {shortLabel ?? key}
                                  </Text>
                                  <Text
                                    size={28}
                                    weight={800}
                                    lh={1.1}
                                    mb={rate !== null ? 4 : 0}
                                    color={isLowest ? "red" : "dark"}
                                  >
                                    {count.toLocaleString("fr-FR")}
                                  </Text>
                                  {rate !== null && (
                                    <>
                                      <Text
                                        size="sm"
                                        weight={700}
                                        sx={{ color: rateColor }}
                                        mb={2}
                                      >
                                        {rate.toFixed(1)}%
                                      </Text>
                                    </>
                                  )}
                                </Box>
                              </Box>
                              {/* Losange séparateur */}
                              {idx < FUNNEL_CONFIG.length - 1 && (
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    background: "#e9ecef",
                                    transform: "rotate(45deg)",
                                    borderRadius: 2,
                                    flexShrink: 0,
                                    margin: "0 6px",
                                    marginTop: 0,
                                  }}
                                />
                              )}
                            </Flex>
                          );
                        })}
                      </Flex>
                    </Box>
                  );
                })()}
              </>
            );
          })()
        )}
      </Box>

      <Divider my="xl" />

      {/* Section: Inscriptions */}
      <Stack spacing="xl">
        <Box>
          <Group spacing="xs" mb="md">
            <ThemeIcon variant="light" color="blue" size="md">
              <TrendingUp size={18} />
            </ThemeIcon>
            <Title order={3}>Évolution des inscriptions</Title>
          </Group>

          <Paper
            shadow="sm"
            p="xl"
            radius="lg"
            sx={{ backgroundColor: "#ffffff" }}
          >
            <Flex
              justify="space-between"
              align="center"
              mb="xl"
              wrap="wrap"
              gap="md"
            >
              <Group spacing="sm">
                <Text size="lg" weight={800} color="dark">
                  Tendance temporelle
                </Text>
                <Badge variant="filled" color="blue" size="md" radius="sm">
                  {processedData.totalFiltered} affichés
                </Badge>
              </Group>

              {/* Filter Controls */}
              <Group
                spacing={8}
                bg="#f8f9fa"
                p={6}
                sx={{ borderRadius: "12px", border: "1px solid #e9ecef" }}
              >
                <Button
                  size="sm"
                  compact
                  variant={userFilter === "all" ? "white" : "subtle"}
                  onClick={() => setUserFilter("all")}
                  color={userFilter === "all" ? "dark" : "gray"}
                  disabled={isLoading}
                  sx={{
                    borderRadius: "8px",
                    boxShadow:
                      userFilter === "all"
                        ? "0 1px 3px rgba(0,0,0,0.1)"
                        : "none",
                  }}
                >
                  Tous
                </Button>
                <Button
                  size="sm"
                  compact
                  variant={userFilter === "students" ? "white" : "subtle"}
                  onClick={() => setUserFilter("students")}
                  color={userFilter === "students" ? "dark" : "gray"}
                  disabled={isLoading}
                  sx={{
                    borderRadius: "8px",
                    boxShadow:
                      userFilter === "students"
                        ? "0 1px 3px rgba(0,0,0,0.1)"
                        : "none",
                  }}
                >
                  Élèves
                </Button>
                <Button
                  size="sm"
                  compact
                  variant={userFilter === "parents" ? "white" : "subtle"}
                  onClick={() => setUserFilter("parents")}
                  color={userFilter === "parents" ? "dark" : "gray"}
                  disabled={isLoading}
                  sx={{
                    borderRadius: "8px",
                    boxShadow:
                      userFilter === "parents"
                        ? "0 1px 3px rgba(0,0,0,0.1)"
                        : "none",
                  }}
                >
                  Parents
                </Button>
              </Group>
            </Flex>

            <Box sx={{ height: "380px" }}>
              {isLoading ? (
                <Flex h="100%" align="center" justify="center">
                  <Loader size="lg" variant="dots" color="blue" />
                </Flex>
              ) : (
                <Line options={options} data={chartData} />
              )}
            </Box>

            <Text align="center" color="dimmed" size="sm" mt="xl" weight={500}>
              Inscriptions d'élèves et de parents au fil du temps
            </Text>
          </Paper>
        </Box>

        <Divider my="xl" />

        {/* Section: Utilisation de l'application */}
        <Box>
          <Group spacing="xs" mb="md">
            <ThemeIcon variant="light" color="violet" size="md">
              <Activity size={18} />
            </ThemeIcon>
            <Title order={3}>Utilisation de l'application</Title>
          </Group>
          <Text size="sm" color="dimmed" mb="lg">
            Analyse de l'engagement des parents avec la plateforme
          </Text>

          {/* Cartes de métriques - toutes en haut */}
          <SimpleGrid
            cols={4}
            spacing="md"
            breakpoints={[
              { maxWidth: "lg", cols: 2 },
              { maxWidth: "sm", cols: 1 },
            ]}
            mb="xl"
          >
            {/* Métriques d'utilisation quotidienne */}
            <UsageMetricCard
              title="Total utilisations"
              value={
                useAppUsageData(
                  parentsData,
                  dateRange,
                  periodFilter,
                  provenanceFilter,
                ).totalUsage
              }
              subtitle="Sur la période"
              icon={<Activity size={20} />}
              color="blue"
            />
            <UsageMetricCard
              title="Parents actifs"
              value={
                useAppUsageData(
                  parentsData,
                  dateRange,
                  periodFilter,
                  provenanceFilter,
                ).activeUsers
              }
              subtitle="Parents ayant utilisé des crédits"
              icon={<Users size={20} />}
              color="green"
            />
            <UsageMetricCard
              title="Moyenne / jour"
              value={
                useAppUsageData(
                  parentsData,
                  dateRange,
                  periodFilter,
                  provenanceFilter,
                ).avgUsagePerDay
              }
              subtitle="Utilisations par jour"
              icon={<ChartLine size={20} />}
              color="cyan"
            />
            <UsageMetricCard
              title="Pic d'utilisation"
              value={
                useAppUsageData(
                  parentsData,
                  dateRange,
                  periodFilter,
                  provenanceFilter,
                ).maxUsageInDay
              }
              subtitle="Maximum en un jour"
              icon={<TrendingUp size={20} />}
              color="orange"
            />
          </SimpleGrid>

          {/* Graphique principal d'utilisation */}
          <AppUsageChart
            parentsData={parentsData}
            isLoading={isLoadingParents}
            dateRange={dateRange}
            periodFilter={periodFilter}
            showCards={false}
            provenanceFilter={provenanceFilter}
          />
        </Box>

        <Stack spacing="xl" mt="xl">
          <Stack spacing="xl" mt="xl">
            {/* Section: Activité quotidienne des élèves */}
            <Box>
              <Group spacing="xs" mb="md">
                <ThemeIcon variant="light" color="teal" size="md">
                  <School size={18} />
                </ThemeIcon>
                <Title order={3}>Activité quotidienne des élèves</Title>
              </Group>
              <Text size="sm" color="dimmed" mb="lg">
                Activités réalisées par les élèves (diagnostic et aide au
                devoir) sur la période sélectionnée
              </Text>
              <StudentActivityChart
                activityData={studentActivityData}
                isLoading={isLoadingStudentActivity}
                dateRange={dateRange}
                periodFilter={periodFilter}
              />
            </Box>

            <Divider my="xl" />

            {/* Section: Analyse Horaire */}
            <Box>
              <AppUsageByHourChart
                parentsData={parentsData}
                isLoading={isLoadingParents}
                dateRange={dateRange}
                showCards={false}
                provenanceFilter={provenanceFilter}
              />
            </Box>

            <Divider my="xl" />

            {/* Section: Engagement des parents */}
            <Box>
              <Group spacing="xs" mb="md">
                <ThemeIcon variant="light" color="green" size="md">
                  <TrendingUp size={18} />
                </ThemeIcon>
                <Title order={3}>Engagement des parents</Title>
              </Group>
              <Text size="sm" color="dimmed" mb="lg">
                Analyse du taux d'engagement et de l'activité des parents sur la
                plateforme (connexions et utilisation de crédits)
              </Text>

              <ParentEngagementChart
                parentsData={parentsData}
                isLoading={isLoadingParents}
                dateRange={dateRange}
                provenanceFilter={provenanceFilter}
              />
            </Box>

            <Divider my="xl" />

            <ClientConversionChart
              prospectsListData={prospectsListData}
              isLoading={isLoadingProspectsList}
              error={errorProspectsList}
              dateRange={dateRange}
              periodFilter={periodFilter}
              provenanceFilter={provenanceFilter}
              chartOptions={options}
            />

            <Divider my="xl" />

            {/* Section: Répartition par niveau scolaire */}
            <Box>
              <Group spacing="xs" mb="md">
                <ThemeIcon variant="light" color="violet" size="md">
                  <ChartPie size={18} />
                </ThemeIcon>
                <Title order={3}>Répartition par niveau scolaire</Title>
              </Group>
              <Text size="sm" color="dimmed" mb="lg">
                Distribution des élèves par classe et niveau sur la période
                sélectionnée
              </Text>

              <Paper
                shadow="sm"
                p="xl"
                radius="lg"
                sx={{ backgroundColor: "#ffffff" }}
              >
                <div style={{ height: "400px", position: "relative" }}>
                  {isLoading ? (
                    <Flex h="100%" align="center" justify="center">
                      <Loader size="lg" variant="dots" color="violet" />
                    </Flex>
                  ) : (
                    <Doughnut
                      data={levelByChartData}
                      options={doughnutOptions}
                    />
                  )}
                </div>

                <Text
                  align="center"
                  color="dimmed"
                  size="sm"
                  mt="xl"
                  weight={500}
                >
                  Répartition totale des élèves par niveau
                </Text>
              </Paper>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default StatistiquesPage;
